import React, { useEffect, useState } from "react";
import { api } from "../../lib/apiClient";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [["line_items", "Parts & Materials"], ["access_hire", "Access Hire"], ["sundries", "Sundries"]];
const WORK_TYPES = ["Fresh Install", "Rip Out", "Repair"];
const LBL = "text-[11px] uppercase tracking-wider font-semibold text-slate-600";
const INP = "h-9 border border-slate-300 rounded px-2 text-sm w-full";

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-2 text-sm cursor-pointer">
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="h-4 w-4 accent-slate-900" />
    {label}
  </label>
);

const toggleIn = (list, v, on) => (on ? [...new Set([...list, v])] : list.filter((x) => x !== v));

const Section = ({ title, children }) => (
  <div className="space-y-2 border-t border-slate-200 pt-3">
    <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-800">{title}</div>
    {children}
  </div>
);

const OverridesEditor = ({ value, onChange, clients }) => (
  <div className="space-y-2">
    {value.map((o, i) => (
      <div key={i} className="flex gap-2 items-center">
        <input list="fa-clients" className={INP} value={o.client} placeholder="Client / labour type" data-testid={`fa-override-client-${i}`}
          onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, client: e.target.value } : x)))} />
        <input type="number" className={INP.replace("w-full", "w-24")} value={o.percent} data-testid={`fa-override-percent-${i}`}
          onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, percent: Number(e.target.value) } : x)))} />
        <span className="text-sm">%</span>
        <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`fa-override-remove-${i}`} className="text-red-600 p-1"><Trash2 size={14} /></button>
      </div>
    ))}
    <Button type="button" variant="outline" size="sm" onClick={() => onChange([...value, { client: "", percent: 35 }])} data-testid="fa-override-add">
      <Plus size={14} className="mr-1" />Add client override
    </Button>
    <datalist id="fa-clients">{clients.map((c) => <option key={c} value={c} />)}</datalist>
  </div>
);

const ClientList = ({ value, onChange }) => (
  <div className="space-y-2">
    {value.map((c, i) => (
      <div key={i} className="flex gap-2 items-center">
        <input list="fa-clients" className={INP} value={c} data-testid={`fa-separate-client-${i}`}
          onChange={(e) => onChange(value.map((x, j) => (j === i ? e.target.value : x)))} />
        <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} data-testid={`fa-separate-remove-${i}`} className="text-red-600 p-1"><Trash2 size={14} /></button>
      </div>
    ))}
    <Button type="button" variant="outline" size="sm" onClick={() => onChange([...value, ""])} data-testid="fa-separate-add">
      <Plus size={14} className="mr-1" />Add client
    </Button>
  </div>
);

export function FloorAuditSettingsDialog({ open, onOpenChange, onSaved }) {
  const [s, setS] = useState(null);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/reports/floor-audit/settings").then(({ data }) => setS(data)).catch(() => toast.error("Could not load report settings."));
    api.get("/branches").then(({ data }) => setClients((data || []).map((b) => b.name))).catch(() => {});
  }, [open]);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { _available_statuses, ...body } = s;
      await api.put("/reports/floor-audit/settings", body);
      toast.success("Report settings saved.");
      onOpenChange(false);
      onSaved?.();
    } catch {
      toast.error("Could not save report settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="floor-audit-settings-dialog">
        <DialogHeader><DialogTitle>Floor Audit report settings</DialogTitle><DialogDescription>Saved for all admins and applied the next time the audit runs.</DialogDescription></DialogHeader>
        {!s ? <div className="text-sm text-slate-500">Loading…</div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <label className={LBL}>Repair minimum hours
                <input type="number" step="0.5" className={`${INP} mt-1`} value={s.repair_min_hours} data-testid="fa-repair-min"
                  onChange={(e) => set("repair_min_hours", Number(e.target.value))} />
              </label>
              <label className={LBL}>Default mark-up %
                <input type="number" className={`${INP} mt-1`} value={s.default_markup} data-testid="fa-default-markup"
                  onChange={(e) => set("default_markup", Number(e.target.value))} />
              </label>
            </div>
            <Section title="Per-client mark-up overrides">
              <p className="text-xs text-slate-500">Clients not listed use their Key Account default mark-up, or the default above.</p>
              <OverridesEditor value={s.markup_overrides} onChange={(v) => set("markup_overrides", v)} clients={clients} />
            </Section>
            <Section title="Clients reported separately (repairs)">
              <Toggle label="Show these clients on the report" checked={s.show_separate_clients} onChange={(v) => set("show_separate_clients", v)} testid="fa-show-separate" />
              <ClientList value={s.separate_clients} onChange={(v) => set("separate_clients", v)} />
            </Section>
            <Section title="Mark-up checks">
              {SECTIONS.map(([k, l]) => (
                <Toggle key={k} label={l} checked={s.markup_sections.includes(k)} testid={`fa-section-${k}`}
                  onChange={(on) => set("markup_sections", toggleIn(s.markup_sections, k, on))} />
              ))}
              <Toggle label="Quote-level mark-up %" checked={s.include_quote_markup} onChange={(v) => set("include_quote_markup", v)} testid="fa-quote-markup" />
              <Toggle label="Flag mark-up above default" checked={s.flag_markup_above} onChange={(v) => set("flag_markup_above", v)} testid="fa-flag-above" />
            </Section>
            <Section title="Install hours checks (work types)">
              {WORK_TYPES.map((w) => (
                <Toggle key={w} label={w} checked={s.install_work_types.includes(w)} testid={`fa-worktype-${w.toLowerCase().replace(/\s+/g, "-")}`}
                  onChange={(on) => set("install_work_types", toggleIn(s.install_work_types, w, on))} />
              ))}
            </Section>
            <Section title="Which quotes">
              <div className="flex gap-4">
                {[["latest", "Latest version only"], ["all", "Every version"]].map(([v, l]) => (
                  <label key={v} className="flex items-center gap-2 text-sm">
                    <input type="radio" name="fa-version" checked={s.version_mode === v} onChange={() => set("version_mode", v)} data-testid={`fa-version-${v}`} />{l}
                  </label>
                ))}
              </div>
              <div className={LBL}>Statuses (none ticked = all)</div>
              <div className="flex flex-wrap gap-4">
                {(s._available_statuses || []).map((st) => (
                  <Toggle key={st} label={st} checked={s.statuses.includes(st)} testid={`fa-status-${st.toLowerCase().replace(/\s+/g, "-")}`}
                    onChange={(on) => set("statuses", toggleIn(s.statuses, st, on))} />
                ))}
              </div>
            </Section>
            <Section title="Display">
              <Toggle label="Show over-floor / over-default figures and net position" checked={s.show_over} onChange={(v) => set("show_over", v)} testid="fa-show-over" />
            </Section>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="fa-settings-cancel">Cancel</Button>
          <Button onClick={save} disabled={!s || saving} data-testid="fa-settings-save">{saving ? "Saving…" : "Save settings"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
